import { ComponentKind, getPrimitiveColumns, ComponentDescription, InputOutputTables } from "../description";
import { SerializedComponent } from "../../components/SerializedApp";
import {
    ComponentDescriptor,
    ComponentBuilder,
    ComponentConfiguratorContext,
    AllowedDataEdits,
    makeEmptyMutableAllowedDataEdits,
} from ".";
import { ComponentHandlerBase } from "./handler";
import { EnumPropertyHandler, ColumnPropertyHandler, ColumnPropertyFlag } from "./property-handlers";
import { PropertySection } from "./property-sections";
import { getDocURL } from "../../docUrl";
import { makeUserProfileArgsForCommentsAndChat } from "../builder-user-profile";
import { makePostCommentAction } from "../builder-utils";
import { commentsTableName } from "../../database-strings";

const ComponentKindComments: ComponentKind = "comments";

enum SortOrder {
    OldestFirst = "oldest-first",
    NewestFirst = "newest-first",
}

const topicPropertyHandler = new ColumnPropertyHandler(
    "topicProperty",
    "Topic",
    [ColumnPropertyFlag.Required, ColumnPropertyFlag.Editable],
    undefined,
    ["topic", "subject", "comments"],
    getPrimitiveColumns,
    "string",
    PropertySection.Data
);
const sortOrderPropertyHandler = new EnumPropertyHandler(
    { sortOrder: SortOrder.OldestFirst },
    "Sort order",
    "Show",
    [
        {
            value: SortOrder.OldestFirst,
            label: "Oldest first",
        },
        {
            value: SortOrder.NewestFirst,
            label: "Newest first",
        },
    ],

    PropertySection.Design,
    "dropdown"
);

export class CommentsComponentHandler extends ComponentHandlerBase<ComponentDescription> {
    constructor() {
        super(ComponentKindComments);
    }

    public entryForTableColumn(
        builder: ComponentBuilder,
        desc: ComponentDescription,
        tables: InputOutputTables
    ): SerializedComponent {
        const { b } = builder;

        const topicProperty = topicPropertyHandler.getColumnName(desc);
        if (topicProperty === undefined) return b.errorUI;

        const sortOrder = sortOrderPropertyHandler.getEnum(desc) === SortOrder.OldestFirst ? "asc" : "desc";

        const { lets, args: userProfileArgs } = makeUserProfileArgsForCommentsAndChat(builder);

        builder.registerTableUsed(commentsTableName);
        return b.let(
            [["topic", b.getProperty(topicProperty, b.getContext, tables.input)], ...lets],
            b.ui("Comments", {
                comments: b.fetchComments(b.getVariable("topic"), b.constant(sortOrder)),
                onTapNew: makePostCommentAction(
                    builder,
                    b.getVariable("topic"),
                    "comment",
                    "writeAComment",
                    "post",
                    "platform-accept"
                ),
                onDelete: b.action("DeleteComments", { topic: b.getVariable("topic") }),
                topic: b.getVariable("topic"),
                ...userProfileArgs,
            })
        );
    }

    public getDescriptor(): ComponentDescriptor<ComponentDescription> {
        return {
            name: "Comments",
            description: "Allows users to post comments",
            img: "componentComments",
            group: "Entry Fields",
            helpUrl: getDocURL("comments"),
            properties: [topicPropertyHandler, sortOrderPropertyHandler, ...this.getBasePropertyDescriptors()],
        };
    }

    // ##userNameEdited:
    // If the user hasn't set a name yet, the Comments component will pop up
    // a modal asking for it, and will then save the name in their user
    // profile, if they have one, which means that the Comments component
    // edits the user profile name column.
    public getAdditionalDataEdits(
        _desc: ComponentDescription,
        _tables: InputOutputTables,
        ccc: ComponentConfiguratorContext
    ): AllowedDataEdits | undefined {
        const userProfile = ccc.userProfileTableInfo;
        if (userProfile === undefined) return undefined;

        const table = ccc.findTable(userProfile.tableName);
        if (table === undefined) return undefined;

        const edits = makeEmptyMutableAllowedDataEdits();
        edits.setColumnsInRow.get(table).add(userProfile.nameColumnName);

        return edits;
    }
}
